/**
 * NDC (National Drug Code) validation utilities
 *
 * Centralized validation to avoid duplication across the codebase
 */

import { z } from "zod";

/**
 * Strict NDC format for storage: 12345-6789-01 (5-4-2 with dashes).
 * Used for database writes and final validation before save.
 */
export const NDC_REGEX = /^\d{5}-\d{4}-\d{2}$/;

/**
 * Loose NDC input regex: accepts 10 or 11 raw digits, or 5-4-2 with dashes.
 * Used for input-field validation (before lookup resolves the format).
 */
export const NDC_INPUT_REGEX = /^(\d{10,11}|\d{5}-\d{4}-\d{2})$/;

/**
 * NDC validation error message
 */
export const NDC_ERROR_MESSAGE = "Invalid NDC format (e.g., 12345-6789-01)";

/**
 * Zod schema for strict NDC validation (storage format)
 */
export const ndcSchema = z
  .string()
  .min(1, "NDC is required")
  .regex(NDC_REGEX, NDC_ERROR_MESSAGE);

/**
 * Zod schema for NDC input (accepts 10/11 digits or formatted 5-4-2)
 */
export const ndcInputSchema = z
  .string()
  .min(1, "NDC is required")
  .regex(NDC_INPUT_REGEX, "Enter 10 or 11 digit NDC");

/**
 * Validate strict NDC format (5-4-2 with dashes) for storage
 */
export function isValidNDC(ndc: string): boolean {
  return NDC_REGEX.test(ndc);
}

/**
 * Validate NDC input (10/11 digits or formatted) for the input field
 */
export function isValidNDCInput(ndc: string): boolean {
  return NDC_INPUT_REGEX.test(ndc);
}

/**
 * Format NDC (add dashes if missing)
 * Input: 12345678901 -> Output: 12345-6789-01
 */
export function formatNDC(ndc: string): string | null {
  // Remove any existing dashes
  const cleaned = ndc.replace(/-/g, "");

  // Check if it's 11 digits
  if (!/^\d{11}$/.test(cleaned)) {
    return null;
  }

  // Format as 5-4-2
  return `${cleaned.slice(0, 5)}-${cleaned.slice(5, 9)}-${cleaned.slice(9, 11)}`;
}

/**
 * Sanitize NDC input (remove extra spaces, convert to lowercase)
 */
export function sanitizeNDC(ndc: string): string {
  return ndc.trim().toUpperCase();
}
